<!-- eslint-disable no-undef -->
<template>
  <div class="lottery-result-card">
    <div class="flag-section" @click="navigateToDetail">
      <img
        v-if="lottery && lottery.game_logo"
        :src="getGameLogoUrl(lottery.game_logo)"
        :alt="`${lottery.country} ${$t('forms.flag')}`"
        class="country-flag"
      />
      <img
        v-else
        src="/img/flags/default.svg"
        :alt="$t('forms.defaultLogo')"
        class="country-flag"
      />
    </div>

    <div class="content-section" @click="navigateToDetail">
      <div class="game-details">
        <h3>{{ lottery?.game_name }}</h3>
        <div class="draw-time">{{ formattedDrawTime }}</div>
      </div>

      <div class="results">
        <!-- Cancelled Overlay - only on results section -->
        <div v-if="isCancelled" class="cancelled-overlay">
          <img :src="cancelledImg" :alt="$t('wagers.cancelled')" class="cancelled-icon" />
        </div>
        
        <div class="numbers-wrap">
          <div 
            v-for="number in mainNumbers" 
            :key="'main-' + number"
            class="number-ball"
          >
            {{ number }}
          </div>
          <div 
            v-for="number in bonusNumbers" 
            :key="'bonus-' + number"
            class="number-ball bonus"
          >
            {{ number }}
          </div>
        </div>
      </div>
    </div>

    <div class="action-section">
      <div class="countdown-box" v-if="isMobile && !isResultsView">
        <div class="countdown-block">
          <div class="countdown-number">{{ String(countdown.hours).padStart(2, '0') }}</div>
          <div class="countdown-label">{{ $t('time.hours') }}</div>
        </div>
        <div class="countdown-block">
          <div class="countdown-number">{{ String(countdown.minutes).padStart(2, '0') }}</div>
          <div class="countdown-label">{{ $t('time.mins') }}</div>
        </div>
        <div class="countdown-block">
          <div class="countdown-number">{{ String(countdown.seconds).padStart(2, '0') }}</div>
          <div class="countdown-label">{{ $t('time.secs') }}</div>
        </div>
      </div>
      <button class="bet-button" v-if="!isMobile || isResultsView" @click="$emit('bet')">
        {{ $t('results.betOnNextDraw') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getGameLogoUrl, parseMainNumbers, parseBonusNumbers } from '@/utils/resultParser';
import { computed, defineProps, ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
// Import cancelled SVG - using public path
const cancelledImg = '/img/cancelled.svg';

const router = useRouter();

const props = defineProps<{
  lottery?: {
    game_name?: string
    game_slug?: string
    draw_date?: string | number | Date
    game_logo?: string
    country?: string
    result?: string // stringified array, e.g. '[[1,2,3,4,5],[6]]'
    status?: string // e.g., 'cancelled', 'completed', 'pending'
    // Add other fields as needed
  }
}>()

const mainNumbers = computed(() => parseMainNumbers(props.lottery?.result));
const bonusNumbers = computed(() => parseBonusNumbers(props.lottery?.result));

// Check if the result is cancelled (empty or null)
const isCancelled = computed(() => {
  const lottery = props.lottery;
  
  // Check explicit status first
  if (lottery?.status === 'cancelled') {
    return true;
  }
  
  const result = lottery?.result;
  
  // Check for various empty/null conditions
  if (!result || result === '' || result === null || result === undefined) {
    return true;
  }
  
  // Check if result is an empty array string
  if (result === '[]' || result === '[[]]' || result === '[[],[]]') {
    return true;
  }
  
  // Check if result is a stringified empty array
  try {
    const parsed = JSON.parse(result);
    if (Array.isArray(parsed)) {
      // Check if it's empty or contains only empty arrays
      if (parsed.length === 0) return true;
      if (parsed.every(arr => Array.isArray(arr) && arr.length === 0)) return true;
    }
  } catch (e) {
    // If parsing fails, treat as cancelled if it's just whitespace
    if (typeof result === 'string' && result.trim() === '') {
      return true;
    }
  }
  
  return false;
});

const formattedDrawTime = computed(() => {
  const drawDate = props.lottery?.draw_date;
  if (!drawDate) return '';

  const dateString = String(drawDate).trim();
  let date: Date;

  // Normalize and parse
  if (dateString.includes('+')) {
    // Example: "2025-07-29 23:51:30+00:00"
    const [datePart, offset] = dateString.split('+');
    date = new Date(datePart.replace(' ', 'T') + '+' + offset);
  } else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)) {
    // "YYYY-MM-DD HH:mm:ss" → Assume UTC
    date = new Date(dateString.replace(' ', 'T') + 'Z');
  } else {
    date = new Date(dateString);
  }

  if (isNaN(date.getTime())) return '';

  // Format as "29/07/2025, 23:51:30" in local time
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
});


const isMobile = computed(() => window.matchMedia('(max-width: 768px)').matches)

const isResultsView = computed(() => {
  // Check if we're in the Results view by looking at the current route
  return window.location.pathname.includes('/results')
})

const truncatedName = computed(() => {
  const game_name = props.lottery?.game_name || ''
  if (isMobile.value) return game_name
  return game_name.length > 15 ? game_name.slice(0, 15) + '...' : game_name
})

// Navigation function
const navigateToDetail = () => {
  if (props.lottery && props.lottery.game_slug) {
    router.push(`/lottery/${props.lottery.game_slug}`);
  }
}

// Countdown logic
const countdown = ref({ hours: 0, minutes: 0, seconds: 0 })
let timer: number
const updateCountdown = () => {
  if (!props.lottery?.draw_date) {
    countdown.value = { hours: 0, minutes: 0, seconds: 0 }
    return
  }
  const now = new Date()
  
  // Parse the UTC date string properly for countdown
  const dateString = String(props.lottery.draw_date)
  let drawTime: Date
  
  if (dateString.includes('+')) {
    // UTC format like "2025-07-29 23:51:30+00:00"
    const datePart = dateString.split('+')[0]
    drawTime = new Date(datePart + 'Z')
  } else {
    // Fallback for other formats
    drawTime = new Date(dateString)
  }
  
  const diff = drawTime.getTime() - now.getTime()
  if (diff <= 0) {
    countdown.value = { hours: 0, minutes: 0, seconds: 0 }
    return
  }
  countdown.value = {
    hours: Math.floor(diff / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000)
  }
}
onMounted(() => {
  countdown.value = { hours: 0, minutes: 0, seconds: 0 }
  updateCountdown()
  timer = window.setInterval(updateCountdown, 1000)
})
onUnmounted(() => {
  if (timer) clearInterval(timer)
})
</script>

<script lang="ts">
export default {
  name: 'LotteryResultCard'
}
</script>

<style scoped>
.lottery-result-card {
  background: var(--bg-left-section, var(--bg-primary));
  overflow: hidden;
  display: grid;
  grid-template-columns: auto 1fr min-content;
  gap: 12px;
  padding: 0;
  align-items: stretch;
  cursor: pointer;
  position: relative;
  border: 1px solid var(--border-color);
}

.cancelled-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  pointer-events: none;
  min-height: 100px;
}

.cancelled-icon {
  width: 120px;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(0,0,0,0.3));
  opacity: 0.9;
}

.flag-section {
  grid-row: span 2;
  display: flex;
  align-items: center;
  padding: 8px 0 8px 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.flag-section:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.country-flag {
  width: 74.54000091552734px;
  height: 74.54000091552734px;
  border-radius: 50%;
  object-fit: cover;
}

.content-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding: 8px 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.content-section:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.game-details h3 {
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 500;
  margin: 0;
}

.draw-time {
  color: var(--text-light);
  font-size: 14px;
  margin: 4px 0 0;
}

.results {
  display: flex;
  gap: 8px;
  position: relative;
}

.numbers-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-width: 100%;
}


.action-section {
  grid-row: span 2;
  display: flex;
  margin: -1px;
}

.bet-button {
  background: var(--bg-right-section, var(--lottery-card-gradient));
  color: var(--text-white);
  border: none;
  padding: 0 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  text-align: center;
  line-height: 1.4;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;
}

.bet-button:hover {
  background: var(--bg-dark-tertiary);
}

@media (max-width: 768px) {
  .lottery-result-card {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: stretch;
    gap: 0;
    border-radius: 8px;
    min-width: 0;
    box-shadow: none;
    background: var(--bg-card);
    border: 1px solid var(--border-color);
    margin-bottom: 8px;
  }

  .flag-section {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px 8px 12px 16px;
    flex: 0 0 auto;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .flag-section:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }

  .country-flag {
    width: 32px;
    height: 32px;
    margin: 0;
    border: 1px solid var(--border-color);
  }

  .content-section {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 4px;
    padding: 6px 4px 6px 4px;
    min-width: 0;
    grid-column: 2;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .content-section:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }

  .game-details h3 {
    font-size: 14px;
    font-weight: 600;
    margin: 0;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 140px;
    line-height: 1.2;
  }

  .game-details .draw-time {
    font-size: 11px;
    color: var(--text-secondary);
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 140px;
    line-height: 1.2;
  }

  .results {
    display: flex !important;
    gap: 6px;
    align-items: center;
    margin-top: 0;
    margin-left: 0;
  }

  .numbers-wrap {
    display: flex;
    gap: 6px;
    align-items: center;
  }

  .action-section {
    background: transparent;
    display: flex;
    align-items: stretch;
    justify-content: flex-end;
    flex: 0 0 auto;
    margin: 0;
    padding: 0;
    width: auto;
    grid-column: 3;
  }

  .bet-button {
    display: flex !important;
    background: var(--bg-dark);
    color: var(--text-white);
    border: none;
    padding: 12px 16px;
    border-radius: 0 8px 8px 0;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease;
    white-space: nowrap;
    min-width: 100px;
    justify-content: center;
    align-items: center;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    height: 100%;
    width: 100%;
  }

  .bet-button:hover {
    background: var(--lottery-card-gradient);
  }

  .countdown-box {
    background: var(--lottery-card-gradient);
    border-radius: 4px;
    padding: 8px 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    min-width: 120px;
    width: 100%;
    margin: 0 auto;
  }
  .countdown-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 32px;
    gap: 2px;
  }
  .countdown-number {
    font-size: 18px;
    font-weight: 700;
    color: var(--text-countdown-digit, #ffffff);
    line-height: 1;
    letter-spacing: 0.5px;
  }
  .countdown-label {
    font-size: 10px;
    color: var(--text-countdown-label-on-band, rgba(255, 255, 255, 0.92));
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    line-height: 1;
    margin-top: 2px;
  }

  .cancelled-icon {
    width: 100px;
    height: 100%;
  }
}
</style>
